import React, { useState } from 'react';
import { Building2, CheckCircle, XCircle, Plus, List, Upload, SlidersHorizontal, Network } from 'lucide-react';
import OrganizationListTab from './components/OrganizationListTab';
import OrganizationBulkUploadTab from './components/OrganizationBulkUploadTab';
import OrganizationFeaturesTab from './components/OrganizationFeaturesTab';
import OrganizationHierarchyTab from './components/OrganizationHierarchyTab';
import useOrganizationStore from '@/stores/SuperAdminStore/OrganizationStore';
import { ErrorDisplay } from '@/Components/common';
import './Organizations.css';

const OrganizationManagementPage: React.FC = () => {
  const {
    organizations,
    error,
    fetchOrganizations,
  } = useOrganizationStore();

  const [activeTab, setActiveTab] = useState<'list' | 'bulk-upload' | 'features' | 'hierarchy'>('list');

  // Calculate stats from organizations data
  const stats = {
    totalOrganizations: organizations.length,
    activeOrganizations: organizations.filter(org => org.isActive).length,
    inactiveOrganizations: organizations.filter(org => !org.isActive).length,
    totalUsers: organizations.reduce((sum, org) => sum + (org.userCount || 0), 0),
  };

  if (error && organizations.length === 0) {
    return (
      <div className="admin-organization-page-container">
        <ErrorDisplay
          title="Failed to Load Organization Data"
          message={error}
          type="error"
          showRetry
          onRetry={() => fetchOrganizations()}
        />
      </div>
    );
  }

  return (
    <div className="admin-organization-page-container">
      <div className="admin-organization-page-breadcrumb">
        <span>Settings</span>
        <span>›</span>
        <span>Organizations</span>
      </div>

      <div className="admin-organization-page-header">
        <h1 className="admin-organization-page-title">Organization Management</h1>
        <p className="admin-organization-page-subtitle">Manage organizations and user assignments</p>
      </div>

      <div className="admin-organization-page-stats">
        <div className="admin-organization-page-stat-card">
          <div className="admin-organization-page-stat-info">
            <h3>Total Organizations</h3>
            <p>{stats.totalOrganizations}</p>
          </div>
          <div className="admin-organization-page-stat-icon orange">
            <Building2 size={24} />
          </div>
        </div>
        <div className="admin-organization-page-stat-card">
          <div className="admin-organization-page-stat-info">
            <h3>Active</h3>
            <p>{stats.activeOrganizations}</p>
          </div>
          <div className="admin-organization-page-stat-icon green">
            <CheckCircle size={24} />
          </div>
        </div>
        <div className="admin-organization-page-stat-card">
          <div className="admin-organization-page-stat-info">
            <h3>Inactive</h3>
            <p>{stats.inactiveOrganizations}</p>
          </div>
          <div className="admin-organization-page-stat-icon gray">
            <XCircle size={24} />
          </div>
        </div>
        <div className="admin-organization-page-stat-card">
          <div className="admin-organization-page-stat-info">
            <h3>Total Users</h3>
            <p>{stats.totalUsers}</p>
          </div>
          <div className="admin-organization-page-stat-icon blue">
            <Plus size={24} />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: '8px',
        marginBottom: '24px',
        borderBottom: '2px solid #e5e7eb',
        paddingBottom: '0'
      }}>
        <button
          onClick={() => setActiveTab('list')}
          style={{
            padding: '12px 24px',
            background: 'none',
            border: 'none',
            borderBottom: activeTab === 'list' ? '2px solid #3b82f6' : '2px solid transparent',
            marginBottom: '-2px',
            cursor: 'pointer',
            fontSize: '15px',
            fontWeight: 500,
            color: activeTab === 'list' ? '#3b82f6' : '#6b7280',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.2s'
          }}
        >
          <List size={18} />
          Manage Organizations
        </button>
        <button
          onClick={() => setActiveTab('bulk-upload')}
          style={{
            padding: '12px 24px',
            background: 'none',
            border: 'none',
            borderBottom: activeTab === 'bulk-upload' ? '2px solid #3b82f6' : '2px solid transparent',
            marginBottom: '-2px',
            cursor: 'pointer',
            fontSize: '15px',
            fontWeight: 500,
            color: activeTab === 'bulk-upload' ? '#3b82f6' : '#6b7280',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.2s'
          }}
        >
          <Upload size={18} />
          Bulk Import
        </button>
        <button
          onClick={() => setActiveTab('features')}
          style={{
            padding: '12px 24px',
            background: 'none',
            border: 'none',
            borderBottom: activeTab === 'features' ? '2px solid #3b82f6' : '2px solid transparent',
            marginBottom: '-2px',
            cursor: 'pointer',
            fontSize: '15px',
            fontWeight: 500,
            color: activeTab === 'features' ? '#3b82f6' : '#6b7280',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.2s'
          }}
        >
          <SlidersHorizontal size={18} />
          Features
        </button>
        <button
          onClick={() => setActiveTab('hierarchy')}
          style={{
            padding: '12px 24px',
            background: 'none',
            border: 'none',
            borderBottom: activeTab === 'hierarchy' ? '2px solid #3b82f6' : '2px solid transparent',
            marginBottom: '-2px',
            cursor: 'pointer',
            fontSize: '15px',
            fontWeight: 500,
            color: activeTab === 'hierarchy' ? '#3b82f6' : '#6b7280',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.2s'
          }}
        >
          <Network size={18} />
          Hierarchy
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'list' && <OrganizationListTab />}
      {activeTab === 'bulk-upload' && <OrganizationBulkUploadTab />}
      {activeTab === 'features' && <OrganizationFeaturesTab />}
      {activeTab === 'hierarchy' && <OrganizationHierarchyTab />}
    </div>
  );
};

export default OrganizationManagementPage;
